import { useEffect, useState } from "react";
import { ArrowUpRight, CalendarDays, FileText, QrCode, Stethoscope, UsersRound } from "lucide-react";
import { Link } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { request } from "@/lib/api";

function timeOfDayGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

export default function HomePage() {
  const { token, user } = useAuth();
  const { name: tenantName } = useTenant();
  const [dashboard, setDashboard] = useState(null);
  const [qrImage, setQrImage] = useState("");

  useEffect(() => {
    request("/portal/dashboard", {}, token)
      .then(setDashboard)
      .catch(() => setDashboard({ tip: null, upcoming_bookings: [], upcoming_consultations: [], upcoming_lab_orders: [] }));
    request(`/portal/qr?url=${encodeURIComponent(window.location.origin)}`).then((data) => setQrImage(data.image)).catch(() => setQrImage(""));
  }, [token]);

  const upcomingCare = [
    ...(dashboard?.upcoming_bookings || []).map((booking) => ({ kind: "booking", date: booking.appointment_date, record: booking })),
    ...(dashboard?.upcoming_consultations || []).map((consultation) => ({ kind: "consultation", date: consultation.appointment_date, record: consultation })),
    // A lab order is a phlebotomist arriving at your door on a given morning,
    // which is as much a plan to keep as any appointment.
    ...(dashboard?.upcoming_lab_orders || []).map((order) => ({ kind: "lab_order", date: order.collection_date, record: order })),
  ].sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  function upcomingCareItem({ kind, record }) {
    if (kind === "booking") {
      return (
        <div className="upcoming-item" key={`booking-${record.booking_id}`} data-testid={`upcoming-booking-${record.booking_id}`}>
          <span>{record.appointment_date}</span>
          <div><strong>{record.package_name}</strong><small>{record.beneficiary_name} · {record.status.replaceAll("_", " ")}</small></div>
        </div>
      );
    }
    if (kind === "lab_order") {
      const count = record.items?.length || 0;
      return (
        <div className="upcoming-item" key={`lab-order-${record.order_id}`} data-testid={`upcoming-lab-order-${record.order_id}`}>
          <span>{record.collection_date}</span>
          <div>
            <strong>{count} lab test{count === 1 ? "" : "s"} · home collection</strong>
            <small>{record.patient?.name} · {record.collection_slot} · {record.status.replaceAll("_", " ")}</small>
          </div>
        </div>
      );
    }
    return (
      <div className="upcoming-item" key={`consultation-${record.consultation_id}`} data-testid={`upcoming-consultation-${record.consultation_id}`}>
        <span>{record.appointment_date}</span>
        <div><strong>{record.specialty} Consultation</strong><small>{record.appointment_slot} · {record.status.replaceAll("_", " ")}</small></div>
      </div>
    );
  }

  return (
    <section className="page-stack home-page" data-testid="home-page">
      <div className="welcome-block" data-testid="welcome-block"><p className="eyebrow">{timeOfDayGreeting()}, {user?.name?.split(" ")[0] || "there"}</p><h1>Your wellbeing, organised.</h1><p>Book a health check, speak to a doctor, or use your exclusive care benefits.</p></div>
      <article className="tip-feature" data-testid="today-health-tip"><img src={dashboard?.tip?.image || "https://images.pexels.com/photos/7578797/pexels-photo-7578797.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"} alt={dashboard?.tip?.topic || "Health tip"} /><div><p className="eyebrow">Today’s health tip</p><h2>{dashboard?.tip?.title || "Small choices, lasting health"}</h2><p>{dashboard?.tip?.summary || "A regular check-up is a simple way to understand your health better."}</p></div></article>
      <div className="section-heading"><div><p className="eyebrow">Start here</p><h2>Take care of what matters</h2></div></div>
      <div className="action-grid" data-testid="home-actions"><Link to="/health-checks" className="action-card action-card--green" data-testid="home-health-checks-link"><CalendarDays size={24} /><strong>Health checks</strong><span>Employee cover and family self-pay care</span><ArrowUpRight size={20} /></Link><Link to="/consultations" className="action-card action-card--sky" data-testid="home-consultations-link"><Stethoscope size={24} /><strong>Talk to a doctor</strong><span>Online care, every day</span><ArrowUpRight size={20} /></Link><Link to="/family" className="action-card action-card--coral" data-testid="home-family-link"><UsersRound size={24} /><strong>My family</strong><span>Save family profiles for quicker bookings</span><ArrowUpRight size={20} /></Link><Link to="/reports" className="action-card action-card--amber" data-testid="home-reports-link"><FileText size={24} /><strong>Reports</strong><span>View your health check reports</span><ArrowUpRight size={20} /></Link></div>
      <section className="upcoming-section" data-testid="upcoming-bookings-section"><div className="section-heading"><div><p className="eyebrow">Your plans</p><h2>Upcoming care</h2></div><Link to="/health-checks" data-testid="view-all-bookings-link">View all</Link></div>{upcomingCare.length ? upcomingCare.map(upcomingCareItem) : <p className="empty-state" data-testid="empty-upcoming-bookings">Your scheduled health checks, consultations and lab collections will appear here.</p>}</section>
      <section className="qr-section" data-testid="portal-qr-section"><div><QrCode size={22} /><h2>Keep Health Hub handy</h2><p>Scan to open this portal quickly from your mobile screen.</p></div>{qrImage && <img src={qrImage} alt={`QR code for ${tenantName} Health Hub`} data-testid="portal-qr-code" />}</section>
    </section>
  );
}